import { createComponentMapper } from '@gro-up/api/utils'

// HERO
export type HeroComponentAlias =
  | 'subHomeHeader'
  | 'locationHero'
  | 'locationHeader'
  | 'neighbourhoodHeader'
  | 'growArticleHeader'
  | 'genericHeader'

// COMPONENT MAPPERS
export type BlockComponentAlias = (
  | 'componentCrossDomain'
  | 'aboutBlock'
  | 'componentUspBlock'
  | 'componentProposition'
  | 'componentInlineListGrid'
  | 'componentInlineList'
  | 'faqOverview'
  | 'faqSection'
  | 'faqBlock'
  | 'faqGrid'
  | 'componentIntroQuote'
  | 'componentServiceCardWrapper'
  | 'componentFunctionalCardWrapper'
  | 'componentFunctionalCardSlider'
  | 'imagesWithContentComponent'
  | 'homeHeader'
  | 'contactInformation'
  | 'corporateContactInformationBlock'
  | 'locationImageOverviewHeader'
  | 'corporateLocationListMap'
  | 'corporateWorkingAreaMap'
  | 'corporateNeighbourhoodMap'
  | 'stickyBar'
  | 'personBlockComponent'
  | 'socialBlockComponent'
  | 'quoteBlockComponent'
  | 'contactComponent'
  | 'activityHeader'
  | 'htmlTextBlock'
  | 'corporateTextBlock'
  | 'corporateMediaBlock'
  | 'embedComponent'
  | 'homeSlides'
  | 'relatedNews'
  | 'newsItemHeader'
  | 'corporateActivitySlider'
  | 'corporateRegiActActivitySlider'
  | 'newsOverviewHeader'
  | 'newsOverview'
  | 'corporateCalculationTool'
  | 'corporateForm'
  | 'corporateFormWithContent'
  | 'componentTourTool'
  | 'corporateGrowArticleSection'
  | 'corporateGrowArticleCarousel'
  | 'relatedGrowArticles'
  | 'slideDeck'
  | 'growArticleOverview'
  | 'corporateGrowPeriodSection'
) & HeroComponentAlias

type UseComponentMappers = {
  getBlockComponent: (alias: string | undefined) => Component | null
  isValidBlockAlias: (alias: string) => alias is BlockComponentAlias
}

const blockComponentMapper = createComponentMapper<BlockComponentAlias>({
  aboutBlock: async () => import('@corporate/components/blocks/AboutBlock.vue'),
  componentUspBlock: async () => (await import('@gro-up/ui/primitives')).UspBlock,
  componentCrossDomain: async () => (await import('@gro-up/ui/components')).CrossDomain,
  componentProposition: async () => import('@corporate/components/blocks/PropositionBlock/PropositionBlock.vue'),
  componentInlineListGrid: async () => (await import('@gro-up/ui/components')).InlineListGrid,
  componentInlineList: async () => (await import('@gro-up/ui/components')).InlineListGrid,
  subHomeHeader: async () => import('@corporate/components/blocks/CorporateSubhomeHero/CorporateSubhomeHero.vue'),
  locationHero: async () => import('@corporate/components/LocationHero/LocationHero.vue'),
  locationHeader: async () => import('@corporate/components/LocationHero/LocationHeaderWrapper.vue'),
  faqOverview: async () => (await import('@gro-up/ui/components')).FaqSection,
  faqSection: async () => (await import('@gro-up/ui/components')).FaqSection,
  corporateFrequentlyAskedQuestions: async () => import('@corporate/components/blocks/FaqBlock/FaqBlock.vue'),
  faqGrid: async () => import('@corporate/components/blocks/FaqBlock/FaqBlock.vue'),
  componentIntroQuote: async () => (await import('@gro-up/ui/components')).IntroQuote,
  componentServiceCardWrapper: async () => (await import('@gro-up/ui/components')).ServiceCardGridBlock,
  componentFunctionalCardWrapper: async () => (await import('@gro-up/ui/components')).FunctionalCardGridBlock,
  homeHeader: async () => import('@corporate/components/blocks/HomeHeaderBlock.vue'),
  contactInformation: async () => import('@corporate/components/ContactInformation/ContactInformationWrapper.vue'),
  corporateContactInformationBlock: async () => import('@corporate/components/ContactInformation/ContactInformationWrapper.vue'),
  locationImageOverviewHeader: async () =>
    import('@corporate/components/LocationMediaOverview/LocationMediaOverviewWrapper.vue'),
  corporateLocationListMap: async () =>
    import('@corporate/components/blocks/LocationListMap/LocationListMap.vue'),
  corporateWorkingAreaMap: async () =>
    import('@corporate/components/blocks/MapListNeighborhoods/MapListNeighborhoods.vue'),
  corporateNeighbourhoodMap: async () =>
    import('@corporate/components/blocks/NeighbourhoodMap/NeighbourhoodMap.vue'),
  stickyBar: async () => import('@corporate/components/blocks/StickyBar/StickyBarBlock.vue'),
  componentFunctionalCardSlider: async () => (await import('@gro-up/ui/components')).CardSlider,
  imagesWithContentComponent: async () => (await import('@gro-up/ui/components')).ImageWithContentBlock,
  neighbourhoodHeader: async () => (await import('@gro-up/ui/components')).CommunityWork,
  personBlockComponent: async () => (await import('@gro-up/ui/components')).MediaContact,
  socialBlockComponent: async () => (await import('@gro-up/ui/components')).MediaContact,
  quoteBlockComponent: async () => import('@corporate/components/blocks/QuoteBlock/QuoteBlock.vue'),
  contactComponent: async () => (await import('@gro-up/ui/components')).ContactBlock,
  activityHeader: async () => import('@corporate/components/blocks/ActivityHero/ActivityHero.vue'),
  htmlTextBlock: async () => import('@corporate/components/blocks/HtmlTextBlock/HtmlTextBlock.vue'),
  corporateTextBlock: async () => import('@corporate/components/blocks/TextBlock/TextBlock.vue'),
  corporateMediaBlock: async () => import('@corporate/components/blocks/MediaBlock/MediaBlock.vue'),
  embedComponent: async () => import('@corporate/components/blocks/EmbedBlock/EmbedBlock.vue'),
  homeSlides: async () => import('@corporate/components/DomainSlides/DomainScroll.vue'),
  relatedNews: async () => import('@corporate/components/blocks/RelatedNews/RelatedNewsBlock.vue'),
  newsItemHeader: async () => import('@corporate/components/blocks/NewsItemHeader/NewsItemHeaderBlock.vue'),
  corporateActivitySlider: async () => import('@corporate/components/blocks/ActivitySlider/ActivitySliderBlock.vue'),
  corporateRegiActActivitySlider: async () => import('@corporate/components/blocks/ActivitySlider/RegiActActivitySliderBlock.vue'),
  newsOverviewHeader: async () => import('@corporate/components/blocks/CenteredTextHero/CenteredTextHero.vue'),
  newsOverview: async () => import('@corporate/components/NewsOverview/NewsOverviewPage.vue'),
  corporateCalculationTool: async () => import('@gro-up/calculator/components/CalculatorBlock.vue'),
  corporateForm: async () => import('@corporate/components/blocks/FormBlock/FormBlock.vue'),
  corporateFormWithContent: async () =>
    import('@corporate/components/blocks/FormWithContentBlock/FormWithContentBlock.vue'),
  componentTourTool: async () => import('@gro-up/tour/components/TourBlock.vue'),
  corporateGrowArticleSection: async () =>
    import('@corporate/components/blocks/GrowArticleSection/GrowArticleSection.vue'),
  corporateGrowArticleCarousel: async () =>
    import('@corporate/components/blocks/GrowArticleCarousel/GrowArticleCarousel.vue'),
  relatedGrowArticles: async () =>
    import('@corporate/components/blocks/GrowArticleRelated/GrowArticleRelatedBlock.vue'),
  corporateGrowPeriodSection: async () => import('@corporate/components/blocks/GrowPeriodSection/GrowPeriodSection.vue'),
  growArticleHeader: async () => import('@corporate/components/blocks/GrowArticleHeader/GrowArticleHeaderBlock.vue'),
  genericHeader: async () => import('@corporate/components/blocks/GenericHeader/GenericHeader.vue'),
  slideDeck: async () => import('@corporate/components/blocks/SlideDeckBlock.vue'),
  growArticleOverview: async () =>
    import('@corporate/components/blocks/GrowArticleOverview/GrowArticleOverviewBlock.vue'),
})

export function useComponentMappers(): UseComponentMappers {
  return {
    getBlockComponent: (alias: string | undefined) => {
      const component = blockComponentMapper.getComponentByAlias(alias)

      if (!component && alias) {
        console.warn(`[ComponentMapper] Unknown component alias: "${alias}". Component will not be rendered.`)
      }

      return component
    },
    isValidBlockAlias: blockComponentMapper.isValidAlias,
  }
}
